import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataSourceOrdenacaoInsensivel } from '@shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CampoAdicionalService } from '@shared/services/apis/servico/campo-adicional.service';
import { CampoAdicionalStruct } from '@shared/services/structs/servico/campo-adicional.struct';
import { HeaderComponent } from '@shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';
import { UtilService } from '@shared/services/util.service';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { GetCampoAdicionalResponse } from '@shared/services/responses/servico/get-campo-adicional.response';
import { ReturnStruct } from '@shared/services/structs/return.struct';

@Component({
  selector: 'app-campos-adicionais',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatTooltipModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    EmptyStateComponent,
    RouterModule,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './campos-adicionais.component.html',
})
export class CamposAdicionaisComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Servicos;
  public model: FormGroup;
  public _paginaAtual: number = 0;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;
  public _dataSource = new DataSourceOrdenacaoInsensivel<CampoAdicionalStruct>();
  public _isLoading: boolean = false;
  public isExcluindoSelecionados: boolean = false;
  public idCampoAdicionalExcluindo: number | null = null;
  public displayedColumns: string[] = ['select', 'nome', 'tipo', 'opcoes', 'obrigatorio', 'actions'];
  public selection = new SelectionModel<CampoAdicionalStruct>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private campoAdicionalService: CampoAdicionalService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({ pesquisa: [''] });
    this.loadFilters();
    this.carregarDados();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/servicos/campos-adicionais/cadastrar',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  public carregarDados(): void {
    this._isLoading = true;
    const nome = this.model.get('pesquisa')?.value || '';
    this.campoAdicionalService.getAll(nome, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => this.tratarRetornoCarregamento(response),
      error: () =>
        this.tratarFalhaCarregamento(
          'Não foi possível carregar os campos adicionais agora. Tente novamente em alguns instantes.'
        ),
    });
  }

  private tratarRetornoCarregamento(response: GetCampoAdicionalResponse): void {
    if (response.isError) {
      this.tratarFalhaCarregamento(
        response.errorDescription || 'Não foi possível carregar os campos adicionais.'
      );
      return;
    }
    this._isLoading = false;
    this._totalRegistros = response.totalRegistros;
    this._dataSource.data = [...this._dataSource.data, ...response.listaCamposAdicionais];
  }

  public textoOpcoes(campo: CampoAdicionalStruct): string {
    if (!campo.listaOpcoes?.length) {
      return '-';
    }
    return campo.listaOpcoes.map(o => o.descricao).join(', ');
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarDados();
  }
  public clickPesquisar(): void {
    this._paginaAtual = 0;
    this.selection.clear();
    this._dataSource.data = [];
    this.carregarDados();
  }
  public isAllSelected(): boolean {
    return (
      this.selection.selected.length === this._dataSource.data.length && this._dataSource.data.length > 0
    );
  }
  public toggleAllRows(): void {
    this.isAllSelected() ? this.selection.clear() : this.selection.select(...this._dataSource.data);
  }

  public abrirModalExcluir(item?: CampoAdicionalStruct): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];
    if (itemsToDelete.length === 0) return;

    const isUnico = itemsToDelete.length === 1;
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: isUnico
          ? `Excluir ${itemsToDelete[0].nome}?`
          : `Excluir ${itemsToDelete.length} campos adicionais?`,
        icon: 'delete',
        description: isUnico
          ? 'O campo deixa de aparecer no formulário e nas impressões da ordem de serviço.'
          : 'Os campos deixam de aparecer no formulário e nas impressões da ordem de serviço.',
        warningMessage: isUnico
          ? 'Esta operação não pode ser desfeita. Todos os dados preenchidos neste campo, em todas as ordens de serviço, serão perdidos.'
          : 'Esta operação não pode ser desfeita. Todos os dados preenchidos nestes campos, em todas as ordens de serviço, serão perdidos.',
        buttonConfirm: isUnico ? 'Excluir campo' : 'Excluir campos',
        buttonClose: 'Cancelar',
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this.excluir(itemsToDelete, isBulk);
      }
    });
  }

  private excluir(itemsToDelete: CampoAdicionalStruct[], isBulk: boolean): void {
    this.iniciarExclusao(itemsToDelete, isBulk);
    this.campoAdicionalService.deleteBulk(itemsToDelete.map(i => i.idCampoAdicional)).subscribe({
      next: r => this.tratarRetornoExclusao(r, itemsToDelete),
      error: () =>
        this.tratarFalha(
          'Não foi possível excluir o campo adicional agora. Tente novamente em alguns instantes.'
        ),
    });
  }

  private iniciarExclusao(itemsToDelete: CampoAdicionalStruct[], isBulk: boolean): void {
    if (isBulk) {
      this.isExcluindoSelecionados = true;
    } else {
      this.idCampoAdicionalExcluindo = itemsToDelete[0].idCampoAdicional;
    }
  }

  private tratarRetornoExclusao(r: ReturnStruct, itemsToDelete: CampoAdicionalStruct[]): void {
    if (r.isError) {
      this.tratarFalha(r.errorDescription || 'Não foi possível excluir o campo adicional.');
      return;
    }
    this.finalizarExclusao();
    const mensagem =
      itemsToDelete.length === 1
        ? 'Campo adicional excluído com sucesso.'
        : 'Campos adicionais excluídos com sucesso.';
    this.alertService.show('Sucesso', mensagem, AlertType.success);
    this.clickPesquisar();
  }

  private tratarFalhaCarregamento(mensagem: string): void {
    if (this._paginaAtual > 0) {
      this._paginaAtual--;
    }

    this.tratarFalha(mensagem);
  }

  private tratarFalha(mensagem: string): void {
    this._isLoading = false;
    this.finalizarExclusao();
    this.alertService.show('Aviso', mensagem, AlertType.warning);
  }

  private finalizarExclusao(): void {
    this.isExcluindoSelecionados = false;
    this.idCampoAdicionalExcluindo = null;
  }
}
